import { useEffect, useMemo, useState } from 'react'
import './styles.css'
import { getQueue, bridgeCall, setOutcome, getDeviceToken, enrollDevice, getStaff, rescheduleCall } from './api'

export default function App() {
  // ---- Enroll state ----
  const [hasToken, setHasToken] = useState(!!getDeviceToken())
  const [code, setCode] = useState('')
  const [deviceNumber, setDeviceNumber] = useState('')

  // ---- App state ----
  const [view, setView] = useState('now') // 'now' | 'log'
  useEffect(() => {
    if (view === 'now') {
      document.body.classList.add('view-now')
      document.body.classList.remove('view-log')
    } else {
      document.body.classList.add('view-log')
      document.body.classList.remove('view-now')
    }
    return () => {
      document.body.classList.remove('view-now', 'view-log')
    }
  }, [view])

  const [staffId, setStaffId] = useState('')
  const [staff, setStaff] = useState([])
  const [staffLoading, setStaffLoading] = useState(false)
  const [staffError, setStaffError] = useState('')
  const [leads, setLeads] = useState([])
  const [logQuery, setLogQuery] = useState('')

  // Load staff
  useEffect(() => {
    if (!hasToken) return
    let stop = false
    const load = async () => {
      try {
        setStaffLoading(true); setStaffError('')
        const s = await getStaff()
        if (!stop) setStaff(Array.isArray(s) ? s.filter(x => x?.active !== false) : [])
      } catch {
        if (!stop) setStaffError('Could not load staff')
      } finally {
        if (!stop) setStaffLoading(false)
      }
    }
    load()
    const id = setInterval(load, 60_000)
    return () => { stop = true; clearInterval(id) }
  }, [hasToken])

  // Poll queue
  useEffect(() => {
    if (!hasToken) return
    let stop = false
    const tick = async () => {
      const q = await getQueue()
      if (!stop) setLeads(q)
    }
    tick()
    const id = setInterval(tick, 3000)
    return () => { stop = true; clearInterval(id) }
  }, [hasToken])

  const onCall = async (leadId) => {
    if (!staffId) { 
      alert('Select your name first')
      return false
    }
    const staffObj = staff.find(s => s.id === staffId)
    await bridgeCall(leadId, staffId, staffObj?.name || '')
    setLeads(await getQueue())
    return true
  }

  const onOutcome = async (leadId, code) => {
  await setOutcome(leadId, staffId || '', code)
  setLeads(await getQueue())
}

const onReschedule = async (leadId, whenISO) => {
  await rescheduleCall(leadId, staffId || '', whenISO)
  setLeads(await getQueue())
}


  // "Now" tab groupings (using display + after_hours)
  const needsCall = useMemo(
    () => leads.filter(l => l.display && !l.after_hours),
    [leads]
  )
  const afterHours = useMemo(
    () => leads.filter(l => l.display && l.after_hours),
    [leads]
  )

  // Log (last 7 days + search)
  const last7d = useMemo(() => {
    const cutoff = Date.now() - 7*24*60*60*1000
    const base = leads.filter(l => (l.received_at ?? 0) >= cutoff)
    const q = logQuery.trim().toLowerCase()
    const filtered = q
      ? base.filter(l =>
          (l.name || '').toLowerCase().includes(q) ||
          (l.phone || '').toLowerCase().includes(q) ||
          (l.source || '').toLowerCase().includes(q) ||
          (l.status || '').toLowerCase().includes(q)
        )
      : base
    return filtered.sort((a,b) => (b.received_at ?? 0) - (a.received_at ?? 0))
  }, [leads, logQuery])

  // ---------- ENROLL ----------
  if (!hasToken) {
    return (
      <div style={{ padding: 16 }}>
        <h2>📱 ClinicCallback — Enroll device</h2>
        <p className="small">Enter the clinic’s device code and this phone’s number.</p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input value={code} onChange={e => setCode(e.target.value.trim())} placeholder="Device code (e.g. CBA-7K3P)" className="select" style={{ width: 220 }} />
          <input value={deviceNumber} onChange={e => setDeviceNumber(e.target.value.trim())} placeholder="Device phone (+44...)" className="select" style={{ width: 220 }} />
          <button className="btn" onClick={async () => {
            try {
              if (!/^\+\d{7,15}$/.test(deviceNumber)) { alert('Enter phone in E.164 format, e.g. +447...'); return }
              await enrollDevice(code, deviceNumber)
              setHasToken(true)
            } catch { alert('Enroll failed. Check code/number and try again.') }
          }}>Enroll</button>
        </div>
      </div>
    )
  }

  // ---------- MAIN ----------
  return (
    <div>
      {/* Sticky header = brand/tabs + staff selector (always) + optional log search */}
      <div className="sticky-header">
        <Topbar
          view={view}
          onViewChange={setView}
        />
      
        <StaffBar
          staffId={staffId}
          staff={staff}
          loading={staffLoading}
          error={staffError}
          onStaffChange={setStaffId}
        />
      
        {view === 'log' && (
          <LogBar
            logQuery={logQuery}
            onLogQueryChange={setLogQuery}
            count={last7d.length}
          />
        )}
      </div>

      {view === 'now' ? (
        <div className="stack" style={{ gap: 16, padding: '0 12px 24px' }}>
          <Section variant="primary" title="Needs Call" count={needsCall.length}>
            <LeadList leads={needsCall} onCall={onCall} onOutcome={onOutcome} onReschedule={onReschedule} emptyText="All caught up 🎉" canCall={!!staffId} />
          </Section>

          {afterHours.length > 0 && (
            <Section variant="muted" title="After Hours" count={afterHours.length}>
              <LeadList
                leads={afterHours}
                onCall={onCall}
                onOutcome={onOutcome}
                onReschedule={onReschedule}
                emptyText="None waiting"
                canCall={!!staffId}
              />
            </Section>
          )}
        </div>
      ) : (
        <div className="stack" style={{ gap: 12, padding: '20px 12px 24px' }}>
          <Section variant="default">
            <LeadList
              leads={last7d}
              onCall={onCall}
              onOutcome={onOutcome}
              onReschedule={onReschedule}
              emptyText="No recent leads"
              canCall={!!staffId}
            />
          </Section>
        </div>
      )}
    </div>
  )
}

function Topbar({ view, onViewChange }) {
  return (
    <div className="topbar">
      <div className="topbar-inner">
        <h2 className="brand">ClinicCallback</h2>
        <div className="btn-group tabs">
          <button
            className={`btn ${view === 'now' ? 'primary' : ''}`}
            onClick={() => onViewChange('now')}
          >
            Now
          </button>
          <button
            className={`btn ${view === 'log' ? 'primary' : ''}`}
            onClick={() => onViewChange('log')}
          >
            Log
          </button>
        </div>
      </div>
    </div>
  )
}

function StaffBar({ staffId, staff, loading, error, onStaffChange }) {
  return (
    <div className="staffbar">
      <label className="small">Staff</label>
      <select
        className="select staff-select"
        value={staffId}
        onChange={e => onStaffChange(e.target.value)}
        disabled={loading || !!error}
        aria-label="Select staff"
      >
        <option value="">
          {loading ? 'Loading…' : error ? 'Error loading staff' : 'Select…'}
        </option>
        {staff.map(s => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
    </div>
  )
}

function LogBar({ logQuery, onLogQueryChange, count }) {
  return (
    <div className="logbar">
      <div className="logbar-inner">
        <div className="logbar-title">
          Log (Last 7 days)
          <span className="section-count">{count}</span>
        </div>
        <input
          className="select logbar-search"
          placeholder="Search name, phone, source, status…"
          value={logQuery}
          onChange={(e) => onLogQueryChange(e.target.value)}
        />
      </div>
    </div>
  )
}

function Section({ title, subtitle, children, variant = 'default', count }) {
  return (
    <section className={`section ${variant}`}>
      <div className="section-accent" />
      {title || subtitle || typeof count === 'number' ? (
        <div className="section-header">
          <div className="section-titles">
            <div className="section-title">
              <span>{title}</span>
              {typeof count === 'number' && (
                <span className="section-count">{count}</span>
              )}
            </div>
            {subtitle && <div className="section-sub">{subtitle}</div>}
          </div>
        </div>
      ) : null}
      <div className="section-body">{children}</div>
    </section>
  )
}

/* ---------- helper components ---------- */

function LeadList({ leads, onCall, onOutcome, onReschedule, emptyText = 'No leads', canCall }) {
  if (!leads?.length) return <div className="card">{emptyText}</div>

  const [openId, setOpenId] = useState(null)
  const toggle = (id) => setOpenId(prev => (prev === id ? null : id))

  return (
    <div className="grid leads">
      {leads.map(l => (
        <LeadCard
          key={l.id}
          lead={l}
          onCall={onCall}
          onOutcome={onOutcome}
          onReschedule={onReschedule}
          canCall={canCall}
          open={openId === l.id}
          onToggle={() => toggle(l.id)}
        />
      ))}
    </div>
  )
}

function LeadCard({ lead, onCall, onOutcome, onReschedule, canCall, open, onToggle }) {
  const [saving, setSaving] = useState(false)
  const [calling, setCalling] = useState(false)

  // --- helper: format Date -> 'YYYY-MM-DDTHH:MM' in LOCAL time (for <input type="datetime-local">)
  const toLocalInputValue = (d) => {
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  // Reschedule modal state
  const [showReschedule, setShowReschedule] = useState(false)
  const [when, setWhen] = useState(() => {
    const t = new Date(Date.now() + 30*60*1000) // default +30m
    return toLocalInputValue(t)
  })

  const handleCall = async () => {
    if (calling) return
    if (!canCall) { alert('Select your name first'); return }
    const ok = await onCall(lead.id)
    if (!ok) return
    setCalling(true)
    setTimeout(() => setCalling(false), 2500)
  }

  const statusText = lead.status || '—'
  const statusLower = statusText.toLowerCase()
  let badgeCls = 'badge gray'
  if (statusLower.includes('booked')) badgeCls = 'badge green'
  else if (statusLower.includes('needs') || statusLower.includes('queued')) badgeCls = 'badge amber'
  else if (statusLower.includes('late') || statusLower.includes('breach')) badgeCls = 'badge red'
  else if (statusLower.includes('attempted') || statusLower.includes('no answer')) badgeCls = 'badge gray'
  else if (statusLower.includes('considering')) badgeCls = 'badge purple'
  else if (statusLower.includes('not fit')) badgeCls = 'badge pink'
  else if (statusLower.includes('after hours')) badgeCls = 'badge violet'
  else if (statusLower.includes('resched')) badgeCls = 'badge blue' // if backend sets "Rescheduled"

  const doOutcome = async (code) => {
    try { setSaving(true); await onOutcome(lead.id, code) }
    finally { setSaving(false) }
  }

  const doReschedule = async () => {
    try {
      setSaving(true)
      // Convert local input string to Date, then to UTC ISO for backend
      await onReschedule(lead.id, new Date(when).toISOString())
      setShowReschedule(false)
    } catch {
      alert('Could not reschedule. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card">
      {/* Header: name + time | Details | status */}
      <div
        className="card-head"
        style={{ display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap:8 }}
      >
        <div>
          <div style={{ fontWeight: 700 }}>{lead.name || 'Unknown'}</div>
          <div className="small">{lead.received_ago}</div>
        </div>

        <button
          className="link-btn details-toggle"
          onClick={onToggle}
          aria-expanded={open}
        >
          Details <span className={`chev ${open ? 'open' : ''}`}>▾</span>
        </button>

        <div className={badgeCls}>{statusText}</div>
      </div>

      {/* Primary actions */}
      <div className="actions">
        <button
          className={`btn call-btn ${calling ? 'calling' : ''}`}
          disabled={saving || calling || !canCall}
          title={!canCall ? 'Select staff first' : undefined}
          onClick={handleCall}
        >
          {calling ? '📞 Calling…' : '📞 Call'}
        </button>

        <div className="outcomes">
          <button className="btn small" disabled={saving} onClick={() => doOutcome('Booked')}>Booked</button>
          <button className="btn small" disabled={saving} onClick={() => doOutcome('Considering')}>Considering</button>
          <button className="btn small" disabled={saving} onClick={() => doOutcome('Not a fit')}>Not a fit</button>
          <button className="btn small" disabled={saving} onClick={() => setShowReschedule(true)}>Reschedule</button>
        </div>
      </div>

      {/* Details content (expands) */}
      <div className={`details ${open ? 'open' : ''}`}>
        <div className="details-grid">
          <div><span className="k">Phone</span><span>{lead.phone || '—'}</span></div>
          <div><span className="k">Service</span><span>{lead.service || '—'}</span></div>
          <div><span className="k">Source</span><span>{lead.source || '—'}</span></div>
          {lead.notes && (
            <div className="full">
              <span className="k">Notes</span>
              <span className="notes">{lead.notes}</span>
            </div>
          )}
        </div>
      </div>

      {/* Reschedule Modal */}
      {showReschedule && (
        <div className="modal-backdrop" onClick={() => setShowReschedule(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Reschedule call</div>
            <div className="stack" style={{ gap: 8 }}>
              <input
                type="datetime-local"
                className="select"
                value={when}
                onChange={e => setWhen(e.target.value)}
                min={toLocalInputValue(new Date(Date.now() + 5*60*1000))}
              />
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button className="btn small" onClick={() => {
                  const t = new Date(Date.now() + 15*60*1000)
                  setWhen(toLocalInputValue(t))
                }}>+15m</button>
                <button className="btn small" onClick={() => {
                  const t = new Date(Date.now() + 30*60*1000)
                  setWhen(toLocalInputValue(t))
                }}>+30m</button>
                <button className="btn small" onClick={() => {
                  const now = new Date()
                  const t = new Date()
                  t.setHours(9,0,0,0)
                  if (now.getHours() >= 9) t.setDate(t.getDate() + 1)
                  setWhen(toLocalInputValue(t))
                }}>Tomorrow 9:00</button>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12, gap: 8, justifyContent:'flex-end' }}>
              <button className="btn" onClick={() => setShowReschedule(false)}>Cancel</button>
              <button className="btn primary" disabled={saving} onClick={doReschedule}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
